import { motion } from "framer-motion";
import { steddyTransition } from "@/lib/animations";
import SteddyStar from "@/components/SteddyStar";

const fadeInUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const HeroSection = () => (
  <section className="hero-gradient relative flex min-h-screen items-center justify-center pb-24 pt-28">
    <div className="container relative z-10 mx-auto px-6 text-center">
      <motion.h1
        {...fadeInUp}
        transition={{ ...steddyTransition, delay: 0.1 }}
        className="font-bold leading-[1.08] text-foreground"
        style={{ fontSize: "clamp(1.8rem, 5.5vw, 4.5rem)" }}
      >
        You Fund the Deal.
      </motion.h1>

      <motion.h1
        {...fadeInUp}
        transition={{ ...steddyTransition, delay: 0.2 }}
        className="mt-2 font-bold leading-[1.08] text-foreground"
        style={{ fontSize: "clamp(1.8rem, 5.5vw, 4.5rem)" }}
      >
        We Run the Comp
        <span className="relative inline-block">
          <span className="text-primary">a</span>
          <SteddyStar className="absolute right-[-0.18em] top-[0.05em] h-2.5 w-2.5 text-primary md:right-[-0.1em] md:top-[0.16em] md:h-3 md:w-3" />
        </span>
        ny.
      </motion.h1>

      <motion.div {...fadeInUp} transition={{ ...steddyTransition, delay: 0.3 }} className="mt-8">
        <a href="/login" className="btn-primary-steddy inline-flex items-center gap-2">
          Deploy Steddy.AI
        </a>
      </motion.div>

      <motion.p
        {...fadeInUp}
        transition={{ ...steddyTransition, delay: 0.4 }}
        className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground"
      >
        Running a funding company is complex — managing it shouldn't be. Steddy.AI, the first
        autonomous back-office for MCA funders, handles underwriting, servicing, and collections so
        you can focus on funding deals.
      </motion.p>

      {/* Dashboard Preview */}
      <motion.div
        {...fadeInUp}
        transition={{ ...steddyTransition, delay: 0.6 }}
        className="mx-auto mt-16 max-w-5xl"
      >
        <div className="card-surface-light overflow-hidden rounded-2xl bg-background p-1">
          <div className="rounded-xl bg-secondary/50 p-6">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-destructive/50" />
                <div className="h-3 w-3 rounded-full" style={{ background: "hsl(45 80% 55%)" }} />
                <div className="h-3 w-3 rounded-full" style={{ background: "hsl(140 60% 50%)" }} />
                <span className="ml-3 font-mono text-xs text-muted-foreground">
                  Steddy.AI — Live Dashboard
                </span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                  Auto-Funding Active
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              {[
                {
                  id: "MCA-8847",
                  merchant: "Verbali Construction",
                  amount: "$185,000",
                  status: "Funded",
                  stage: "Servicing",
                },
                {
                  id: "MCA-8848",
                  merchant: "Pinnacle Roofing Co.",
                  amount: "$72,500",
                  status: "Underwriting",
                  stage: "AI Review",
                },
                {
                  id: "MCA-8849",
                  merchant: "Atlas Freight Lines",
                  amount: "$340,000",
                  status: "Approved",
                  stage: "Contract Sent",
                },
                {
                  id: "MCA-8850",
                  merchant: "Greenfield Dental",
                  amount: "$95,000",
                  status: "Collecting",
                  stage: "Day 47/120",
                },
                {
                  id: "MCA-8851",
                  merchant: "Eastside Auto Body",
                  amount: "$210,000",
                  status: "Funded",
                  stage: "Servicing",
                },
              ].map((row, i) => (
                <motion.div
                  key={row.id}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...steddyTransition, delay: 0.7 + i * 0.08 }}
                  className="steddy-transition grid grid-cols-3 items-center gap-4 rounded-lg px-4 py-3 text-sm hover:bg-background md:grid-cols-5"
                >
                  <span className="hidden font-mono text-xs text-muted-foreground md:block">
                    {row.id}
                  </span>
                  <span className="font-medium text-foreground">{row.merchant}</span>
                  <span className="font-mono font-semibold text-primary">{row.amount}</span>
                  <span>
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        row.status === "Funded"
                          ? "bg-primary/10 text-primary"
                          : row.status === "Approved"
                            ? "bg-primary/10 text-primary"
                            : row.status === "Collecting"
                              ? "bg-accent/10 text-accent"
                              : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {row.status}
                    </span>
                  </span>
                  <span className="hidden text-right text-xs text-muted-foreground md:block">
                    {row.stage}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default HeroSection;
